import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productService } from '../../services/api/product.service';

// Async thunks
export const fetchAllProducts = createAsyncThunk(
  'product/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await productService.getAllProducts(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProductsByLobId = createAsyncThunk(
  'product/fetchByLobId',
  async ({ lobId, params }, { rejectWithValue }) => {
    try {
      const response = await productService.getProductsByLobId(lobId, params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'product/fetchById',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productService.getProductById(productId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProductWithSections = createAsyncThunk(
  'product/fetchWithSections',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productService.getProductWithSections(productId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createProduct = createAsyncThunk(
  'product/create',
  async (productData, { rejectWithValue }) => {
    try {
      const response = await productService.createProduct(productData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'product/update',
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const response = await productService.updateProduct(productId, productData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'product/delete',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productService.deleteProduct(productId);
      return { productId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const activateProduct = createAsyncThunk(
  'product/activate',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productService.activateProduct(productId);
      return { productId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deactivateProduct = createAsyncThunk(
  'product/deactivate',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productService.deactivateProduct(productId);
      return { productId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProductStatus = createAsyncThunk(
  'product/updateStatus',
  async ({ productId, status }, { rejectWithValue }) => {
    try {
      const response = await productService.updateProductStatus(productId, status);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  products: [],
  currentProduct: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  filters: {
    search: '',
    lobId: null,
    sortBy: 'display_order',
    sortOrder: 'asc',
  },
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  successMessage: null,
};

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetProductState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Fetch all Products
    builder
      .addCase(fetchAllProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = {
            currentPage: action.payload.pagination.currentPage,
            totalPages: action.payload.pagination.totalPages,
            totalItems: action.payload.pagination.totalItems,
            itemsPerPage: action.payload.pagination.itemsPerPage,
          };
        }
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch Products';
      });

    // Fetch Products by LOB ID
    builder
      .addCase(fetchProductsByLobId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductsByLobId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = {
            currentPage: action.payload.pagination.currentPage,
            totalPages: action.payload.pagination.totalPages,
            totalItems: action.payload.pagination.totalItems,
            itemsPerPage: action.payload.pagination.itemsPerPage,
          };
        }
      })
      .addCase(fetchProductsByLobId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch Products';
      });

    // Fetch Product by ID
    builder
      .addCase(fetchProductById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProduct = action.payload.data;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch Product';
      });

    // Fetch Product with Sections
    builder
      .addCase(fetchProductWithSections.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductWithSections.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProduct = action.payload.data;
      })
      .addCase(fetchProductWithSections.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch Product with sections';
      });

    // Create Product
    builder
      .addCase(createProduct.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.isCreating = false;
        state.products.push(action.payload.data);
        state.successMessage = action.payload.message || 'Product created successfully';
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload?.message || 'Failed to create Product';
      });

    // Update Product
    builder
      .addCase(updateProduct.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.products.findIndex(
          (product) => product.product_id === action.payload.data.product_id
        );
        if (index !== -1) {
          state.products[index] = action.payload.data;
        }
        state.currentProduct = action.payload.data;
        state.successMessage = action.payload.message || 'Product updated successfully';
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload?.message || 'Failed to update Product';
      });

    // Delete Product
    builder
      .addCase(deleteProduct.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.products = state.products.filter(
          (product) => product.product_id !== action.payload.productId
        );
        state.successMessage = action.payload.message || 'Product deleted successfully';
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload?.message || 'Failed to delete Product';
      });

    // Activate Product
    builder
      .addCase(activateProduct.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(activateProduct.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.products.findIndex(
          (product) => product.product_id === action.payload.productId
        );
        if (index !== -1) {
          state.products[index].is_active = true;
        }
        state.successMessage = action.payload.message || 'Product activated successfully';
      })
      .addCase(activateProduct.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload?.message || 'Failed to activate Product';
      });

    // Deactivate Product
    builder
      .addCase(deactivateProduct.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(deactivateProduct.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.products.findIndex(
          (product) => product.product_id === action.payload.productId
        );
        if (index !== -1) {
          state.products[index].is_active = false;
        }
        state.successMessage = action.payload.message || 'Product deactivated successfully';
      })
      .addCase(deactivateProduct.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload?.message || 'Failed to deactivate Product';
      });

    // Update Product Status
    builder
      .addCase(updateProductStatus.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateProductStatus.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.products.findIndex(
          (product) => product.product_id === action.payload.data.product_id
        );
        if (index !== -1) {
          state.products[index] = action.payload.data;
        }
        state.successMessage = action.payload.message || 'Product status updated successfully';
      })
      .addCase(updateProductStatus.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload?.message || 'Failed to update Product status';
      });
  },
});

export const {
  setFilters,
  clearCurrentProduct,
  clearError,
  clearSuccessMessage,
  resetProductState,
} = productSlice.actions;

export default productSlice.reducer;
